import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  IDropdownItem,
  Input,
  ISelectOption,
  Preloader,
  Select,
  Tabs,
  Toggle,
  Tooltip,
  Link,
  BackwardIcon,
  H3,
  InfoBlock,
  TextArea,
  Label,
  Search,
  LinkIcon,
} from '@smartshell/ui-kit';
import { truncate } from 'lodash';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthContext } from 'auth';
import { ImageUploaderFieldMobile } from 'components';
import { FeaturesContext, useIsFeatureAvailable } from 'features';
import { useWindowResize } from 'hooks';
import { LINKS } from 'links';
import { LocalizationContext } from 'localization';
import { TemplateModal, TemplatesPlaysModal } from 'modals';
import { isSandbox } from 'sandbox';
import {
  ONBOARDING_INSERT_SHORTCUT_PATH_EVENT,
  ONBOARDING_SHORTCUT_CREATED_EVENT,
  OnboardingShortcutCreatedDetail,
} from 'sandbox/onboarding/events';
import {
  GET_SHORTCUTS,
  IGetShortcutGroupsResponse,
  IShortcutGroup,
  SHORTCUT_GROUPS,
} from 'schemas';
import notification from 'services/notificationService';
import { ApplicationType, ESyncScope, IShortcutTemplate } from 'types';

import s from './ApplicationManager.module.scss';
import { PlayScreenshots } from './components';
import ApplicationTooltip from './components/ApplicationTooltip';
import { RemoveButton } from './components/RemoveButton';
import { SaveButton } from './components/SaveButton';
import { AGE_OPTIONS, DEFAULT_VALUES, MAX_SCREENSHOTS } from './consts';
import { ImageUploaderField } from './ImageUploaderField';
import {
  ICreateShortcutRequest,
  ICreateShortcutResponse,
  IDeleteShortcutRequest,
  IDeleteShortcutResponse,
  IFormValues,
  IGetShortcutRequest,
  IGetShortcutResponse,
  IShortcutApiInput,
  IShortcutTemplateRequest,
  IShortcutTemplateResponse,
  IShortcutTemplatesResponse,
  IUpdateShortcutRequest,
  IUpdateShortcutResponse,
} from './interfaces';
import {
  CREATE_BILLING_SHORTCUT,
  DELETE_BILLING_SHORTCUT,
  GET_SHORTCUT,
  GET_SHORTCUT_TEMPLATE,
  GET_SHORTCUT_TEMPLATES,
  UPDATE_BILLING_SHORTCUT,
} from './schemas';
import { useGameAccountsOptions } from './useGameAccountsOptions';
import {
  applyTemplateToForm,
  getFormTitlesConfig,
  setWorkingDirectoryPath,
} from './utils';

export const ApplicationManager: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const isFreeRunAvailable = useIsFeatureAvailable('free_run');
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const size = useWindowResize();
  const { shortcuts_sync_enabled } = useContext(FeaturesContext);

  const PAGE_TITLES_MAP: Record<ApplicationType, string> = {
    GAME: t('application.game.create'),
    APPLICATION: t('application.app.create'),
  };

  const FORM_TITLES_CONFIG = useMemo(() => getFormTitlesConfig(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    setValue,
    getValues,
    watch,
  } = useForm<IFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });
  const { fields: pathFields } = useFieldArray<IFormValues, 'path'>({
    control,
    name: 'path',
  });

  const [appType, setAppType] = useState<ApplicationType>('APPLICATION');
  const [shortcutTemplates, setShortcutTemplates] = useState<
    IShortcutTemplate[] | null | undefined
  >(null);

  const [openTemplatesPlays, setOpenTemplatesPlay] = useState<boolean>(false);
  const [openTemplate, setOpenTemplate] = useState<number | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);

  const { isLoaded: isGameAccountsLoaded, options: gameAccountsGroups } =
    useGameAccountsOptions();

  const { data: shortcutGroups, loading: shortcutGroupsLoading } =
    useQuery<IGetShortcutGroupsResponse>(SHORTCUT_GROUPS, {
      fetchPolicy: 'no-cache',
    });

  const defaultShortcutGroup = useMemo<IShortcutGroup | null>(() => {
    if (!shortcutGroups || !shortcutGroups?.shortcutGroups) return null;

    return shortcutGroups.shortcutGroups.filter(
      (group) => group.groupType === 'DEFAULT'
    )[0];
  }, [shortcutGroups]);

  const shortcutGroupOption = useMemo<IDropdownItem[] | null>(() => {
    if (!shortcutGroups || !shortcutGroups?.shortcutGroups) return null;

    return shortcutGroups.shortcutGroups
      .filter((group) => group.groupType === appType)
      .map(({ id, title }) => ({
        value: id,
        title: truncate(title, { length: 42, separator: '...' }),
      }));
  }, [shortcutGroups, appType]);

  const firstGamePath = watch('path.0.value');
  const onlyRegistered = watch('only_for_registered');
  const ageRating = watch('age_rating');
  const template_title = watch('template_title');
  const templateId = watch('template_id');

  useEffect(() => {
    if (!isSandbox) return;

    const insertDemoPath = (): void => {
      const demoPath = 'C:\\Games\\Demo\\game.exe';
      setValue('path.0.value', demoPath, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setWorkingDirectoryPath(demoPath, setValue);
    };

    window.addEventListener(
      ONBOARDING_INSERT_SHORTCUT_PATH_EVENT,
      insertDemoPath
    );
    return () =>
      window.removeEventListener(
        ONBOARDING_INSERT_SHORTCUT_PATH_EVENT,
        insertDemoPath
      );
  }, [setValue]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryAppType = searchParams.get('type');
  const queryAppId = Number(searchParams.get('id'));

  const backwardLink = useMemo(() => {
    let link = `/applications?type=${appType}`;

    if (queryAppId) {
      link += `&id=${queryAppId}`;
    }

    return link;
  }, [appType, queryAppId]);

  const [getShortcut, { data: loadedShortcut, loading: getShortcutLoading }] =
    useLazyQuery<IGetShortcutResponse, IGetShortcutRequest>(GET_SHORTCUT, {
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (
      queryAppId &&
      loadedShortcut?.shortcut.id !== queryAppId &&
      isGameAccountsLoaded &&
      shortcutGroupOption !== null
    ) {
      getShortcut({ variables: { id: queryAppId } });
      return;
    }

    if (loadedShortcut?.shortcut.id === queryAppId) {
      return;
    }

    if (queryAppType === 'GAME') {
      setAppType(queryAppType);
    }
  }, [queryAppType, queryAppId, isGameAccountsLoaded, shortcutGroupOption]);

  const gameAccountGroupOptions = useMemo<Array<ISelectOption>>(() => {
    if (!loadedShortcut)
      return permissions?.has('game_account_browse') ? gameAccountsGroups : [];

    const { game_account_group } = loadedShortcut.shortcut;

    if (!game_account_group)
      return permissions?.has('game_account_browse') ? gameAccountsGroups : [];

    const { id, title } = game_account_group;

    return permissions?.has('game_account_browse')
      ? gameAccountsGroups
      : [{ value: id, title: title || '' }];
  }, [loadedShortcut, gameAccountsGroups, permissions]);

  useEffect(() => {
    if (
      !loadedShortcut ||
      !loadedShortcut.shortcut ||
      !isGameAccountsLoaded ||
      !shortcutGroupOption ||
      !permissions
    )
      return;

    const {
      shortcut: {
        type,
        title,
        args,
        path,
        main_picture,
        screenshots,
        description,
        game_account_group,
        icon_path,
        run_as_admin,
        work_dir,
        age_rating,
        only_for_registered,
        free_run,
        group_id,
      },
    } = loadedShortcut;

    setAppType(type);

    const pathList = path.map((value) => ({ value }));

    if (pathList.length < 3) {
      for (let i = pathList.length; i < 3; i++) {
        pathList.push({ value: '' });
      }
    }

    const gameAccountGroupOptions = permissions?.has('game_account_browse')
      ? gameAccountsGroups
      : [];

    if (game_account_group && !permissions?.has('game_account_browse')) {
      gameAccountGroupOptions.push({
        value: game_account_group?.id,
        title: game_account_group?.title || '',
      });
    }

    const accountsGroup =
      gameAccountGroupOptions.find(
        (option) => option.value === game_account_group?.id
      )?.value || null;

    const shortcutGroup =
      shortcutGroupOption?.find(({ value }) => value === group_id)?.value ||
      null;

    reset({
      ...DEFAULT_VALUES,
      title,
      path: pathList,
      run_as_admin,
      accountsGroup,
      ...(args ? { args } : {}),
      ...(type === 'GAME' && main_picture ? { main_picture } : {}),
      ...(type === 'APPLICATION' && icon_path ? { icon_path } : {}),
      ...(type === 'GAME' && description ? { description } : {}),
      ...(type === 'GAME' && screenshots
        ? { screenshots: { value: screenshots } }
        : {}),
      template_id: loadedShortcut.shortcut.template_id ?? undefined,
      template_title: shortcutTemplates?.find(
        (temp) => temp?.id === loadedShortcut?.shortcut?.template_id
      )?.title,
      workingDirectory: work_dir ?? '',
      age_rating: age_rating ?? 0,
      only_for_registered,
      free_run,
      group: shortcutGroup,
    });
  }, [loadedShortcut, shortcutGroupOption, isGameAccountsLoaded, permissions]);

  const { formFieldsConfig, pageTitle } = useMemo(
    () => ({
      formFieldsConfig: FORM_TITLES_CONFIG[appType],
      pageTitle: loadedShortcut
        ? t('application.edit.title', loadedShortcut.shortcut.title)
        : PAGE_TITLES_MAP[appType],
    }),
    [appType, loadedShortcut, t]
  );

  const isGame = queryAppType === 'GAME';

  const [createShortcut, { loading: isCreateShortcutProcessing }] = useMutation<
    ICreateShortcutResponse,
    ICreateShortcutRequest
  >(CREATE_BILLING_SHORTCUT, {
    refetchQueries: [GET_SHORTCUTS],
    awaitRefetchQueries: true,
    onCompleted: ({ createShortcut: { id, type } }) => {
      if (isSandbox && type === 'GAME' && templateId) {
        window.dispatchEvent(
          new CustomEvent<OnboardingShortcutCreatedDetail>(
            ONBOARDING_SHORTCUT_CREATED_EVENT,
            { detail: { shortcutId: id } }
          )
        );
      }
      navigate(`/applications?type=${type}&id=${id}`);
      notification.info({
        t,
        title: t(
          isGame ? 'application.game.created' : 'application.app.created'
        ),
        type: 'success',
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
      });
    },
  });

  const [updateShortcut, { loading: isUpdateShortcutProcessing }] = useMutation<
    IUpdateShortcutResponse,
    IUpdateShortcutRequest
  >(UPDATE_BILLING_SHORTCUT, {
    refetchQueries: [GET_SHORTCUTS],
    awaitRefetchQueries: true,
    onCompleted: ({ updateShortcut: { id, type } }) => {
      navigate(`/applications?type=${type}&id=${id}`);
      notification.info({
        t,
        title: t(
          isGame ? 'application.game.updated' : 'application.app.updated'
        ),
        type: 'success',
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
      });
    },
  });

  const [deleteShortcut, { loading: isDeleteShortcutProcessing }] = useMutation<
    IDeleteShortcutResponse,
    IDeleteShortcutRequest
  >(DELETE_BILLING_SHORTCUT, {
    refetchQueries: [GET_SHORTCUTS],
    awaitRefetchQueries: true,
    onCompleted: ({ deleteShortcut: { type } }) => {
      navigate(`/applications?type=${type}`, { replace: true });
      notification.info({
        t,
        title: t(
          isGame ? 'application.game.deleted' : 'application.app.deleted'
        ),
        type: 'success',
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const { data: shortcutTemplatesData } = useQuery<IShortcutTemplatesResponse>(
    GET_SHORTCUT_TEMPLATES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [getTemplateById, { data: templateData, loading: getTemplateLoading }] =
    useLazyQuery<IShortcutTemplateResponse, IShortcutTemplateRequest>(
      GET_SHORTCUT_TEMPLATE,
      {
        fetchPolicy: 'cache-and-network',
      }
    );

  useEffect(() => {
    if (openTemplate) {
      getTemplateById({
        variables: {
          shortcutTemplateId: openTemplate,
        },
      });
    }
  }, [openTemplate]);

  useEffect(() => {
    setShortcutTemplates(shortcutTemplatesData?.shortcutTemplates);
  }, [shortcutTemplatesData]);

  const TEMPLATE_OPTIONS: IDropdownItem[] = useMemo(() => {
    return (
      shortcutTemplates?.map((short) => ({
        title: short?.title || '',
        value: short?.id || '',
        original: t('application.pattern'),
      })) || []
    );
  }, [shortcutTemplates]);

  const loading = useMemo<boolean>(() => {
    return (
      (Boolean(queryAppId) && !loadedShortcut) ||
      getShortcutLoading ||
      shortcutGroupsLoading
    );
  }, [shortcutGroupsLoading, loadedShortcut, queryAppId, getShortcutLoading]);

  const onSubmit = (scope: ESyncScope | undefined): Promise<void> =>
    handleSubmit((values): void => {
      const game_account_group_id = Number(values.accountsGroup);

      const input: IShortcutApiInput = {
        title: values.title,
        paths: {
          value: values.path.map((p) => p.value).filter(Boolean),
        },
        game_account_group_id: game_account_group_id || null,
        args: values.args ?? null,
        ...(appType === 'GAME' ? { main_picture: values.main_picture } : {}),
        ...(appType === 'APPLICATION' ? { icon_path: values.icon_path } : {}),
        ...(appType === 'GAME'
          ? {
              description: values.description,
              screenshots: values.screenshots?.value?.length
                ? { value: values.screenshots.value }
                : undefined,
            }
          : {}),

        ...(values.template_id ? { template_id: values.template_id } : {}),

        work_dir: values.workingDirectory || null,
        only_for_registered: values.only_for_registered,
        age_rating: values.age_rating,
        run_as_admin: values.run_as_admin,
        free_run: values.free_run,
        group_id:
          values.group !== null
            ? Number(values.group)
            : defaultShortcutGroup?.id || 0,
      };

      if (loadedShortcut) {
        const { id } = loadedShortcut.shortcut;
        updateShortcut({
          variables: {
            id,
            sync: scope ? scope : undefined,
            input: {
              ...input,
            },
          },
        });
      } else {
        createShortcut({
          variables: {
            sync: scope ? scope : undefined,
            input: {
              ...input,
              type: appType,
              popular: true,
            },
          },
        });
      }
    })();

  return (
    <>
      {openTemplatesPlays && (
        <TemplatesPlaysModal
          onClose={() => setOpenTemplatesPlay(false)}
          shortcutTemplates={shortcutTemplates}
          setOpenTemplate={setOpenTemplate}
          onApplyTemplate={(template) =>
            applyTemplateToForm(
              template,
              getValues,
              setValue,
              loadedShortcut ? 'update' : 'create'
            )
          }
          getTemplateById={getTemplateById}
          getTemplateLoading={getTemplateLoading}
        />
      )}
      {openTemplate && (
        <TemplateModal
          onClose={() => {
            setOpenTemplate(null);
          }}
          templateData={templateData?.shortcutTemplate}
          loading={getTemplateLoading}
          setOpenTemplatesPlay={setOpenTemplatesPlay}
          onApplyTemplate={(template) =>
            applyTemplateToForm(
              template,
              getValues,
              setValue,
              loadedShortcut ? 'update' : 'create'
            )
          }
        />
      )}
      <div className={s['page-container']}>
        <NavLink to={backwardLink} className={s.link}>
          <BackwardIcon />
          {t('back')}
        </NavLink>

        <Preloader loading={loading}>
          <header className={s['page-title']}>{pageTitle}</header>
          <div
            className={s.form}
            data-onboarding="shortcut-form"
            data-onboarding-template-applied={String(Boolean(templateId))}
            data-onboarding-path-ready={String(
              (firstGamePath?.trim().length ?? 0) >= 6
            )}
          >
            <div className={s['form-column-container']}>
              <div className={s.field}>
                {appType === 'GAME' ? (
                  <div className={s.header_block}>
                    <H3>{formFieldsConfig.title.title}</H3>
                    {template_title && (
                      <span className={s.template_title}>
                        {template_title}
                        <LinkIcon size="s" />
                      </span>
                    )}
                  </div>
                ) : (
                  <H3>{formFieldsConfig.title.title}</H3>
                )}
                {appType === 'GAME' ? (
                  <div
                    className={s.title_block}
                    data-onboarding="shortcut-template-controls"
                  >
                    <div className={s.search_input}>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <Search
                            {...field}
                            onChange={(value) => {
                              field.onChange(
                                typeof value === 'string'
                                  ? value
                                  : TEMPLATE_OPTIONS.find(
                                      (o) => o.value === value
                                    )?.title ?? ''
                              );

                              setTitleError(null);
                            }}
                            onInputChange={(value) => {
                              field.onChange(value);
                              const trimmed = value.trim();

                              if (trimmed.length === 0) {
                                setTitleError(t('errors.required'));
                                return;
                              }

                              if (trimmed.length < 2) {
                                setTitleError(t('errors.minLength', 2));
                                return;
                              }

                              if (trimmed.length > 50) {
                                setTitleError(t('errors.maxLength', 50));
                                return;
                              }

                              setTitleError(null);
                            }}
                            className={s.search}
                            items={TEMPLATE_OPTIONS}
                            placeholder={formFieldsConfig.title.placeholder}
                            bordered
                            allowCustomValue
                            disableClear
                            maxLength={50}
                            renderItem={(item) => (
                              <div
                                onClick={() => {
                                  if (typeof item.value === 'number') {
                                    getTemplateById({
                                      variables: {
                                        shortcutTemplateId: item.value,
                                      },
                                    }).then((res) => {
                                      if (res?.data?.shortcutTemplate) {
                                        applyTemplateToForm(
                                          res.data.shortcutTemplate,
                                          getValues,
                                          setValue,
                                          loadedShortcut ? 'update' : 'create'
                                        );
                                      }
                                    });
                                  }
                                }}
                                className={s.search_item}
                              >
                                <span>{item.title}</span>
                                <Label text={t('application.pattern')} />
                              </div>
                            )}
                          />
                        )}
                      />
                      {titleError !== null && (
                        <span className={s.text}>{titleError}</span>
                      )}
                    </div>

                    <Button
                      variant="secondary"
                      iconName="TemplateIcon"
                      onClick={() => setOpenTemplatesPlay(true)}
                      isLoading={getTemplateLoading}
                      className={s.template_button}
                    >
                      {t('host.start_command.modal.templates')}
                    </Button>
                  </div>
                ) : (
                  <Controller
                    name="title"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        variant="secondary"
                        error={error?.message}
                        placeholder={formFieldsConfig.title.placeholder}
                        bordered
                      />
                    )}
                    rules={{
                      required: t('errors.required'),
                      minLength: {
                        value: 2,
                        message: t('errors.minLength', 2),
                      },
                      maxLength: {
                        value: 50,
                        message: t('errors.maxLength', 50),
                      },
                    }}
                  />
                )}
              </div>
              {appType === 'GAME' && (
                <div className={s.field}>
                  <H3>{formFieldsConfig.description.title}</H3>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextArea
                        className={s.textarea}
                        {...field}
                        variant="secondary"
                        title={'2112'}
                        error={error?.message}
                        placeholder={formFieldsConfig.description.placeholder}
                        bordered
                        limit={4000}
                      />
                    )}
                    rules={{
                      required: t('errors.required'),
                      minLength: {
                        value: 2,
                        message: t('errors.minLength', 2),
                      },
                      maxLength: {
                        value: 4000,
                        message: t('errors.maxLength', 4000),
                      },
                    }}
                  />
                </div>
              )}

              {appType === 'GAME' && (
                <Controller
                  render={({ field }) => (
                    <PlayScreenshots
                      value={field.value}
                      onChange={field.onChange}
                      maxCount={MAX_SCREENSHOTS}
                    />
                  )}
                  name="screenshots.value"
                  control={control}
                />
              )}

              <div className={s.field}>
                <H3>{formFieldsConfig.group.title}</H3>
                <Controller
                  name="group"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Select
                        {...field}
                        bordered
                        placeholder={formFieldsConfig.group.placeholder}
                        options={shortcutGroupOption || []}
                        disabled={
                          !shortcutGroupOption || !shortcutGroupOption.length
                        }
                      />
                      {(!shortcutGroupOption ||
                        !shortcutGroupOption.length) && (
                        <span className={s.group__none}>
                          {formFieldsConfig.group.info}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className={s.field} data-onboarding="shortcut-path">
                <H3>
                  {
                    <div className={s.field_flex}>
                      <span>{formFieldsConfig.path.title}</span>
                      <Tooltip
                        header={formFieldsConfig.path?.tooltipHeader}
                        content={formFieldsConfig.path?.tooltipContent ?? ''}
                        contentClassName={
                          size.width < 960 ? s.tooltip : undefined
                        }
                        footer={
                          <Link
                            title={t('more')}
                            link={LINKS.application[lang]}
                          />
                        }
                        position={size.width > 960 ? 'bottom-left' : 'bottom'}
                      />
                    </div>
                  }
                </H3>
                {pathFields.map((pathField, index) => (
                  <Controller
                    key={index}
                    name={`path.${index}.value`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        variant="secondary"
                        error={error?.message}
                        placeholder={formFieldsConfig.path.placeholder}
                        onBlur={() =>
                          setWorkingDirectoryPath(firstGamePath, setValue)
                        }
                        onEnter={() =>
                          setWorkingDirectoryPath(firstGamePath, setValue)
                        }
                        bordered
                      />
                    )}
                    defaultValue={pathField.value}
                    rules={{
                      ...(index === 0
                        ? { required: t('errors.required') }
                        : {}),
                      minLength: {
                        value: 6,
                        message: t('errors.minLength', 6),
                      },
                      maxLength: {
                        value: 256,
                        message: t('errors.maxLength', 256),
                      },
                    }}
                  />
                ))}
              </div>
              {appType === 'APPLICATION' && (
                <div className={s.field}>
                  <H3>
                    <span>{formFieldsConfig.icon_path.title}</span>
                    <Tooltip
                      header={formFieldsConfig.icon_path.title}
                      content={t('application.iconPath.tooltip')}
                    />
                  </H3>
                  <Controller
                    name="icon_path"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        variant="secondary"
                        placeholder={formFieldsConfig.icon_path.placeholder}
                        error={error?.message}
                        bordered
                      />
                    )}
                    rules={{
                      minLength: {
                        value: 6,
                        message: t('errors.minLength', 6),
                      },
                      maxLength: {
                        value: 256,
                        message: t('errors.maxLength', 256),
                      },
                    }}
                  />
                </div>
              )}
              <div className={s.field}>
                <H3>{formFieldsConfig.args.title}</H3>
                <Controller
                  name="args"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      variant="secondary"
                      placeholder={formFieldsConfig.args.placeholder}
                      bordered
                    />
                  )}
                />
              </div>
              <div className={s.field}>
                <H3>{formFieldsConfig.workingDirectory.title}</H3>
                <Controller
                  name="workingDirectory"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      variant="secondary"
                      placeholder={
                        formFieldsConfig.workingDirectory.placeholder
                      }
                      error={error?.message}
                      bordered
                    />
                  )}
                  rules={{
                    minLength: {
                      value: 5,
                      message: t('errors.minLength', 5),
                    },
                    maxLength: {
                      value: 254,
                      message: t('errors.maxLength', 254),
                    },
                  }}
                />
              </div>
              <div className={s.field}>
                <H3>{formFieldsConfig.accountsGroup.title}</H3>
                <Controller
                  name="accountsGroup"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={
                        gameAccountGroupOptions.length
                          ? formFieldsConfig.accountsGroup.placeholder
                          : t('application.groups.noAvailable')
                      }
                      options={gameAccountGroupOptions}
                      bordered
                      disabled={
                        !gameAccountGroupOptions.length ||
                        !permissions?.has('game_account_browse')
                      }
                    />
                  )}
                />
              </div>
              {appType === 'GAME' && (
                <div className={s.field}>
                  <H3>{t('application.age.title')}</H3>
                  <Controller
                    name="only_for_registered"
                    control={control}
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        iconName="CancelIcon"
                        label={t('application.age.label')}
                        description={t('application.age.description')}
                        className={s.toggle}
                      />
                    )}
                  />
                  {onlyRegistered && (
                    <Controller
                      name="age_rating"
                      control={control}
                      render={({ field }) => (
                        <Tabs {...field} options={AGE_OPTIONS} />
                      )}
                    />
                  )}
                </div>
              )}
              <div className={s.toggle_block}>
                <Controller
                  name="run_as_admin"
                  control={control}
                  render={({ field }) => (
                    <Toggle
                      {...field}
                      iconName="GearManIcon"
                      label={t('application.adminRun')}
                    />
                  )}
                />
                <Controller
                  name="free_run"
                  control={control}
                  render={({ field }) => (
                    <Toggle
                      {...field}
                      iconName="StartIcon"
                      label={t('application.freeRun')}
                      disabled={!isFreeRunAvailable}
                    />
                  )}
                />
              </div>
              {shortcuts_sync_enabled && (
                <InfoBlock
                  title={t('header.error.time.title')}
                  message={t('application.manager.info.text')}
                  className={s.info_block}
                />
              )}
              {!shortcuts_sync_enabled ? (
                <div
                  className={s['buttons-container']}
                  data-onboarding="shortcut-create"
                >
                  {loadedShortcut?.shortcut && (
                    <Button
                      variant="outline"
                      color="red"
                      onClick={() => {
                        deleteShortcut({
                          variables: { id: loadedShortcut.shortcut.id },
                        });
                      }}
                      fullWidth
                    >
                      {t('delete')}
                    </Button>
                  )}
                  <Button
                    onClick={() => onSubmit(undefined)}
                    fullWidth
                    disabled={!isDirty || isDeleteShortcutProcessing}
                    isLoading={
                      isCreateShortcutProcessing || isUpdateShortcutProcessing
                    }
                  >
                    {t(loadedShortcut ? 'save' : 'create')}
                  </Button>
                </div>
              ) : (
                <div
                  className={s['buttons-container']}
                  data-onboarding="shortcut-create"
                >
                  {loadedShortcut?.shortcut && (
                    <RemoveButton
                      onDelete={(sync) => {
                        if (loadedShortcut) {
                          deleteShortcut({
                            variables: {
                              id: loadedShortcut.shortcut.id,
                              sync,
                            },
                          });
                        }
                      }}
                    />
                  )}
                  <SaveButton
                    loadedShortcut={loadedShortcut}
                    onSyncScopeChange={(scope) => {
                      onSubmit(scope);
                    }}
                  />
                </div>
              )}
            </div>
            {appType === 'GAME' && (
              <div className={s['picture']}>
                <div className={s.title}>
                  <H3>{t('application.game.image')}</H3>
                  <Tooltip
                    header={t('application.game.image.size.title')}
                    content={<ApplicationTooltip />}
                    footer={t('application.game.image.size.help')}
                  />
                </div>
                <Controller
                  name="main_picture"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    if (size.width > 960)
                      return (
                        <ImageUploaderField
                          value={value}
                          onChange={onChange}
                          age={onlyRegistered ? ageRating : null}
                        />
                      );
                    return (
                      <ImageUploaderFieldMobile
                        value={value}
                        onChange={onChange}
                        title={t('application.mobile.image')}
                      />
                    );
                  }}
                />
              </div>
            )}
          </div>
        </Preloader>
      </div>
    </>
  );
};
